import type {
    SearchFilterOption,
    ResultFilterOption,
    SearchResultItem,
} from 'owa-timestream-asset-search';
import {
    filterLabel_futureEvents,
    filterLabel_pastEvents,
} from '../calendarItemStrings.locstring.json';
import {
    searchFilterLabel_searchIn,
    searchFilterLabel_dateFrom,
    searchFilterLabel_dateTo,
    searchFilterLabel_hasAttachment,
} from 'owa-locstrings/lib/strings/searchFilterLabel.locstring.json';
import {
    getAllCalendars,
    getDefaultCalendar,
    Calendar,
    isLocalCalendarEntry,
    getCalendarFolderId,
} from 'owa-calendar-cache';
import { today, compare, userDate, subYears, addYears } from 'owa-datetime';
import type CalendarItem from 'owa-service/lib/contract/CalendarItem';
import loc from 'owa-localize';

export const CalendarAssetFilterSearchOption = {
    ToDate: 'ToDate',
    FromDate: 'FromDate',
    Folders: 'Folders',
    Attachments: 'Attachments',
};

export const CalendarAssetFilterResultOption = {
    FutureEvents: 'FutureEvents',
    PastEvents: 'PastEvents',
};

export function getSearchFilterOptions(): SearchFilterOption[] {
    const calendars = getAllCalendars();

    // Find the default calendar, and push it to the top of the collection (if not already first)
    let defaultCalendar: Calendar | null = getDefaultCalendar();
    let index = -1;
    if (defaultCalendar !== null) {
        for (const calendar of calendars) {
            if (calendar.id.id === defaultCalendar.id.id) {
                index = calendars.indexOf(calendar);
                break;
            }
        }
    }
    if (index > 0) {
        calendars.splice(0, 0, calendars.splice(index, 1)[0]);
    }

    // Filter out the non-local calendars
    let filteredCalendars = calendars.filter(c => isLocalCalendarEntry(getCalendarFolderId(c)));

    const folderOptions = filteredCalendars.map(calendar => {
        return {
            key: getCalendarFolderId(calendar),
            text: calendar.displayName ?? '',
        };
    });

    return [
        {
            id: CalendarAssetFilterSearchOption.Folders,
            label: loc(searchFilterLabel_searchIn),
            type: 'Dropdown',
            options: folderOptions,
        },
        {
            id: CalendarAssetFilterSearchOption.FromDate,
            label: loc(searchFilterLabel_dateFrom),
            type: 'DatePicker',
            value: subYears(today(), 1),
        },
        {
            id: CalendarAssetFilterSearchOption.ToDate,
            label: loc(searchFilterLabel_dateTo),
            type: 'DatePicker',
            value: addYears(today(), 2),
        },
        {
            id: CalendarAssetFilterSearchOption.Attachments,
            label: loc(searchFilterLabel_hasAttachment),
            type: 'Checkbox',
        },
    ];
}

export function getResultFilterOptions(): ResultFilterOption[] {
    return [
        {
            id: CalendarAssetFilterResultOption.FutureEvents,
            label: loc(filterLabel_futureEvents),
            filter: filterFutureEvents,
        },
        {
            id: CalendarAssetFilterResultOption.PastEvents,
            label: loc(filterLabel_pastEvents),
            filter: filterPastEvents,
        },
    ];
}

function filterFutureEvents(existingAsset: SearchResultItem): boolean {
    const date = today();
    const calendarItem = existingAsset.data as CalendarItem;
    const parsedStart = userDate(calendarItem.Start || '');
    return compare(parsedStart, date) >= 0;
}

function filterPastEvents(existingAsset: SearchResultItem): boolean {
    const date = today();
    const calendarItem = existingAsset.data as CalendarItem;
    const parsedStart = userDate(calendarItem.Start || '');
    return compare(parsedStart, date) < 0;
}
